import { useState } from 'react'
import type { ChangeEvent, DragEvent, ReactNode } from 'react'
import { ChevronDown, ChevronUp, GripVertical, Layers, Pencil, Plus, Trash2, X } from 'lucide-react'
import { Button, FileDropzone, SegmentedControl, TextField } from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { AppError } from '@shared/api/error'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { titleFromVideoFile } from '../model/members-area.presentation'
import { LessonVideoField } from './LessonVideoField'
import type {
  MembersAreaDetail,
  MembersAsset,
  MembersLesson,
  MembersLessonKind,
  MembersModule,
  MembersReleaseRule,
} from '../model/members-area.contract'
import {
  useCreateMembersLesson,
  useCreateMembersModule,
  useDeleteMembersLesson,
  useDeleteMembersModule,
  useOrderMembersLessons,
  useOrderMembersModules,
  useUpdateMembersLesson,
  useUpdateMembersModule,
} from '../model/members-area.mutations'
import {
  addLessonButton,
  editorStack,
  courseActions,
  emptyEditor,
  fieldGroup,
  fieldLabel,
  formCard,
  formFooter,
  formFooterActions,
  formHeader,
  formTitle,
  iconButton,
  iconRow,
  lessonBody,
  dragHandle,
  lessonCard,
  lessonCardMain,
  lessonCardMeta,
  lessonCardTitle,
  lessonList,
  moduleBlock,
  moduleHeader,
  moduleHeading,
  moduleLabel,
  moduleList,
  moduleTitle,
  muted,
} from './members-area.css'

const LESSON_KIND_OPTIONS = [
  { value: 'text', label: 'Texto' },
  { value: 'pdf', label: 'PDF' },
  { value: 'video', label: 'Vídeo' },
] as const satisfies ReadonlyArray<{ value: MembersLessonKind; label: string }>

const RELEASE_OPTIONS = [
  { value: 'immediate', label: 'Imediata' },
  { value: 'days_after_purchase', label: 'Após dias' },
  { value: 'fixed_date', label: 'Data fixa' },
] as const satisfies ReadonlyArray<{ value: MembersReleaseRule['type']; label: string }>

type LessonDraft = {
  title: string
  kind: MembersLessonKind
  body: string
  releaseType: MembersReleaseRule['type']
  days: string
  date: string
}

type LessonFormTarget = { moduleId: string; lessonId: string | null }

type PendingDelete =
  | { type: 'module'; module: MembersModule }
  | { type: 'lesson'; moduleId: string; lesson: MembersLesson }

const emptyLessonDraft: LessonDraft = {
  title: '',
  kind: 'text',
  body: '',
  releaseType: 'immediate',
  days: '7',
  date: '',
}

function lessonToDraft(lesson: MembersLesson): LessonDraft {
  return {
    title: lesson.title,
    kind: lesson.kind,
    body: lesson.body,
    releaseType: lesson.releaseRule.type,
    days: lesson.releaseRule.type === 'days_after_purchase' ? String(lesson.releaseRule.days) : '7',
    date: lesson.releaseRule.type === 'fixed_date' ? lesson.releaseRule.date : '',
  }
}

function draftToReleaseRule(draft: LessonDraft): MembersReleaseRule {
  if (draft.releaseType === 'days_after_purchase') {
    return { type: 'days_after_purchase', days: Math.max(1, Number(draft.days) || 1) }
  }
  if (draft.releaseType === 'fixed_date') {
    return { type: 'fixed_date', date: draft.date || new Date().toISOString().slice(0, 10) }
  }
  return { type: 'immediate' }
}

const LESSON_KIND_LABELS: Record<MembersLessonKind, string> = {
  text: 'Texto',
  pdf: 'PDF',
  video: 'Vídeo',
}

function lessonKindLabel(lesson: MembersLesson) {
  if (lesson.kind !== 'video') return LESSON_KIND_LABELS[lesson.kind]
  const status = lesson.video?.status
  if (status === 'READY') return 'Vídeo'
  if (status === 'PROCESSING') return 'Vídeo processando'
  if (status === 'FAILED') return 'Vídeo com erro'
  return 'Vídeo pendente'
}

function releaseLabel(rule: MembersReleaseRule) {
  if (rule.type === 'days_after_purchase') {
    return `Libera ${rule.days} ${rule.days === 1 ? 'dia' : 'dias'} após a compra`
  }
  if (rule.type === 'fixed_date') {
    const [year, month, day] = rule.date.split('-')
    return `Libera em ${day}/${month}/${year}`
  }
  return 'Liberação imediata'
}

function reorderIds(ids: string[], fromId: string, toId: string) {
  const from = ids.indexOf(fromId)
  const to = ids.indexOf(toId)
  if (from < 0 || to < 0 || from === to) return null
  const next = [...ids]
  const moved = next[from]
  if (moved === undefined) return null
  next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

function moveIds(ids: string[], id: string, direction: -1 | 1) {
  const index = ids.indexOf(id)
  const next = index + direction
  if (index < 0 || next < 0 || next >= ids.length) return null
  const copy = [...ids]
  const current = copy[index]
  const swap = copy[next]
  if (current === undefined || swap === undefined) return null
  copy[index] = swap
  copy[next] = current
  return copy
}

function notifyError(error: unknown) {
  toast.error(
    error instanceof AppError || error instanceof Error
      ? error.message
      : 'Não foi possível salvar.',
  )
}

function lessonNeedsVideoDraft(lesson: MembersLesson | undefined, kind: MembersLessonKind) {
  return kind === 'video' && lesson?.video?.status !== 'READY'
}

export function CourseEditor({
  productId,
  detail,
  renderLessonAssets,
  onLessonFiles,
}: {
  productId: string
  detail: MembersAreaDetail
  renderLessonAssets: (lessonId: string, assets: MembersAsset[]) => ReactNode
  onLessonFiles: (files: FileList | null, lessonId: string) => void
}) {
  const createModule = useCreateMembersModule(productId)
  const updateModule = useUpdateMembersModule(productId)
  const deleteModule = useDeleteMembersModule(productId)
  const orderModules = useOrderMembersModules(productId)
  const createLesson = useCreateMembersLesson(productId)
  const updateLesson = useUpdateMembersLesson(productId)
  const deleteLesson = useDeleteMembersLesson(productId)
  const orderLessons = useOrderMembersLessons(productId)

  const [moduleDraft, setModuleDraft] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<{ moduleId: string; title: string } | null>(null)
  const [lessonForm, setLessonForm] = useState<LessonFormTarget | null>(null)
  const [lessonDraft, setLessonDraft] = useState<LessonDraft>(emptyLessonDraft)
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null)
  const [draggingLessonId, setDraggingLessonId] = useState<string | null>(null)
  const [dropLessonId, setDropLessonId] = useState<string | null>(null)
  const [videoUploading, setVideoUploading] = useState(false)

  const savingLesson = createLesson.isPending || updateLesson.isPending

  async function confirmModule() {
    const title = moduleDraft?.trim()
    if (!title) return
    try {
      await createModule.mutateAsync(title)
      setModuleDraft(null)
      toast.success('Módulo criado.')
    } catch (error) {
      notifyError(error)
    }
  }

  async function confirmRename() {
    if (!renaming) return
    const title = renaming.title.trim()
    const module = detail.modules.find((item) => item.id === renaming.moduleId)
    if (!title || title === module?.title) {
      setRenaming(null)
      return
    }
    try {
      await updateModule.mutateAsync({ moduleId: renaming.moduleId, title })
      setRenaming(null)
    } catch (error) {
      notifyError(error)
    }
  }

  async function confirmPendingDelete() {
    if (!pendingDelete) return
    try {
      if (pendingDelete.type === 'module') {
        await deleteModule.mutateAsync(pendingDelete.module.id)
        if (lessonForm?.moduleId === pendingDelete.module.id) setLessonForm(null)
      } else {
        await deleteLesson.mutateAsync({
          moduleId: pendingDelete.moduleId,
          lessonId: pendingDelete.lesson.id,
        })
        if (lessonForm?.lessonId === pendingDelete.lesson.id) setLessonForm(null)
      }
      setPendingDelete(null)
    } catch (error) {
      notifyError(error)
    }
  }

  async function moveModule(moduleId: string, direction: -1 | 1) {
    const orderedIds = moveIds(
      detail.modules.map((item) => item.id),
      moduleId,
      direction,
    )
    if (!orderedIds) return
    try {
      await orderModules.mutateAsync(orderedIds)
    } catch (error) {
      notifyError(error)
    }
  }

  function startLessonDrag(lessonId: string, event: DragEvent<HTMLDivElement>) {
    if (event.target instanceof Element && event.target.closest('button')) {
      event.preventDefault()
      return
    }
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', lessonId)
    setDraggingLessonId(lessonId)
  }

  function overLesson(lessonId: string, event: DragEvent<HTMLDivElement>) {
    if (!draggingLessonId || draggingLessonId === lessonId) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDropLessonId(lessonId)
  }

  async function dropLesson(
    module: MembersModule,
    lessonId: string,
    event: DragEvent<HTMLDivElement>,
  ) {
    event.preventDefault()
    const fromId = event.dataTransfer.getData('text/plain')
    setDraggingLessonId(null)
    setDropLessonId(null)
    const orderedIds = reorderIds(
      module.lessons.map((item) => item.id),
      fromId,
      lessonId,
    )
    if (!orderedIds) return
    try {
      await orderLessons.mutateAsync({ moduleId: module.id, orderedIds })
    } catch (error) {
      notifyError(error)
    }
  }

  function openNewLesson(moduleId: string) {
    setLessonDraft(emptyLessonDraft)
    setLessonForm({ moduleId, lessonId: null })
  }

  function openEditLesson(moduleId: string, lesson: MembersLesson) {
    setLessonDraft(lessonToDraft(lesson))
    setLessonForm({ moduleId, lessonId: lesson.id })
  }

  async function saveLesson() {
    if (!lessonForm) return
    const title = lessonDraft.title.trim()
    if (!title) return
    const releaseRule = draftToReleaseRule(lessonDraft)
    try {
      if (lessonForm.lessonId === null) {
        const lesson = await createLesson.mutateAsync({
          moduleId: lessonForm.moduleId,
          title,
          kind: lessonDraft.kind,
        })
        if (lessonDraft.body || releaseRule.type !== 'immediate') {
          await updateLesson.mutateAsync({
            moduleId: lessonForm.moduleId,
            lessonId: lesson.id,
            body: lessonDraft.body,
            releaseRule,
          })
        }
        if (lessonDraft.kind === 'video') {
          setLessonDraft((current) => ({ ...current, title }))
          setLessonForm({ moduleId: lessonForm.moduleId, lessonId: lesson.id })
          toast.success('Aula criada. Agora envie o vídeo.')
          return
        }
        toast.success('Aula criada.')
      } else {
        const current = detail.modules
          .flatMap((module) => module.lessons)
          .find((item) => item.id === lessonForm.lessonId)
        await updateLesson.mutateAsync({
          moduleId: lessonForm.moduleId,
          lessonId: lessonForm.lessonId,
          title,
          kind: lessonDraft.kind,
          body: lessonDraft.body,
          releaseRule,
          ...(lessonNeedsVideoDraft(current, lessonDraft.kind) ? { status: 'DRAFT' } : {}),
        })
        toast.success('Aula salva.')
      }
      setLessonForm(null)
    } catch (error) {
      notifyError(error)
    }
  }

  async function ensureVideoLesson(file: File) {
    if (!lessonForm) throw new Error('Não foi possível criar a aula.')
    const title = lessonDraft.title.trim() || titleFromVideoFile(file)
    const releaseRule = draftToReleaseRule(lessonDraft)
    try {
      if (lessonForm.lessonId === null) {
        const lesson = await createLesson.mutateAsync({
          moduleId: lessonForm.moduleId,
          title,
          kind: 'video',
        })
        if (releaseRule.type !== 'immediate') {
          await updateLesson.mutateAsync({
            moduleId: lessonForm.moduleId,
            lessonId: lesson.id,
            releaseRule,
          })
        }
        setLessonDraft((current) => ({ ...current, title, kind: 'video' }))
        return lesson.id
      }
      await updateLesson.mutateAsync({
        moduleId: lessonForm.moduleId,
        lessonId: lessonForm.lessonId,
        title,
        kind: 'video',
        body: lessonDraft.body,
        releaseRule,
        status: 'DRAFT',
      })
      return lessonForm.lessonId
    } catch (error) {
      notifyError(error)
      throw error
    }
  }

  function showVideoLesson(lessonId: string) {
    if (!lessonForm) return
    setLessonForm({ moduleId: lessonForm.moduleId, lessonId })
  }

  function deleteCopy(pending: PendingDelete) {
    if (pending.type === 'lesson') {
      return {
        title: 'Excluir esta aula?',
        description: `A aula "${pending.lesson.title}" será removida do curso.`,
        confirmLabel: 'Excluir aula',
      }
    }
    const count = pending.module.lessons.length
    const description =
      count === 0
        ? `O módulo "${pending.module.title}" será removido do curso.`
        : count === 1
          ? `O módulo "${pending.module.title}" e a aula dele serão removidos do curso.`
          : `O módulo "${pending.module.title}" e as ${count} aulas serão removidos do curso.`
    return {
      title: 'Excluir este módulo?',
      description,
      confirmLabel: 'Excluir módulo',
    }
  }

  function renderLessonForm(lesson: MembersLesson | null) {
    const editing = lesson !== null
    return (
      <div className={formCard} data-testid="members-lesson-editor">
        <div className={formHeader}>
          <span className={formTitle}>{editing ? 'Editar aula' : 'Nova aula'}</span>
          <button
            type="button"
            className={iconButton}
            aria-label="Cancelar aula"
            disabled={videoUploading}
            onClick={() => {
              if (!videoUploading) setLessonForm(null)
            }}
          >
            <X size={16} />
          </button>
        </div>
        <TextField
          label="Título da aula"
          placeholder="Ex.: Boas-vindas ao curso"
          autoFocus
          value={lessonDraft.title}
          onChange={(event) =>
            setLessonDraft((current) => ({ ...current, title: event.target.value }))
          }
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !videoUploading) setLessonForm(null)
          }}
        />
        <div className={fieldGroup}>
          <span className={fieldLabel}>Formato</span>
          <SegmentedControl
            value={lessonDraft.kind}
            options={LESSON_KIND_OPTIONS}
            disabled={Boolean(lesson?.video) || videoUploading}
            onChange={(kind) => setLessonDraft((current) => ({ ...current, kind }))}
            ariaLabel="Formato da aula"
          />
          {lesson?.video ? (
            <span className={muted}>Remova o vídeo para trocar o formato da aula.</span>
          ) : null}
        </div>
        {lessonDraft.kind === 'text' ? (
          <label className={fieldGroup}>
            <span className={fieldLabel}>Conteúdo</span>
            <textarea
              className={lessonBody}
              placeholder="Escreva o conteúdo da aula."
              value={lessonDraft.body}
              onChange={(event) =>
                setLessonDraft((current) => ({ ...current, body: event.target.value }))
              }
            />
          </label>
        ) : lessonDraft.kind === 'video' ? (
          <LessonVideoField
            productId={productId}
            lesson={editing && lesson && lesson.kind === 'video' ? lesson : null}
            onFileSelected={ensureVideoLesson}
            onLessonReady={showVideoLesson}
            onUploadingChange={setVideoUploading}
          />
        ) : editing && lesson ? (
          <div className={fieldGroup}>
            <FileDropzone
              id={`members-lesson-file-${lesson.id}`}
              label="PDF da aula"
              accept="application/pdf,.pdf"
              helperText="PDF de até 1 GB."
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                onLessonFiles(event.target.files, lesson.id)
                event.target.value = ''
              }}
            />
            {lesson.assets.length > 0 ? renderLessonAssets(lesson.id, lesson.assets) : null}
          </div>
        ) : (
          <p className={muted}>Salve a aula para anexar o PDF.</p>
        )}
        <div className={fieldGroup}>
          <span className={fieldLabel}>Liberação</span>
          <SegmentedControl
            value={lessonDraft.releaseType}
            options={RELEASE_OPTIONS}
            onChange={(releaseType) => setLessonDraft((current) => ({ ...current, releaseType }))}
            ariaLabel="Liberação da aula"
          />
          {lessonDraft.releaseType === 'days_after_purchase' ? (
            <TextField
              label="Dias após a compra"
              type="number"
              min={1}
              value={lessonDraft.days}
              onChange={(event) =>
                setLessonDraft((current) => ({ ...current, days: event.target.value }))
              }
            />
          ) : null}
          {lessonDraft.releaseType === 'fixed_date' ? (
            <TextField
              label="Data de liberação"
              type="date"
              value={lessonDraft.date}
              onChange={(event) =>
                setLessonDraft((current) => ({ ...current, date: event.target.value }))
              }
            />
          ) : null}
        </div>
        <div className={formFooter}>
          {lessonDraft.kind === 'video' ? (
            <span className={muted}>A aula é concluída quando o aluno assiste 90% do vídeo.</span>
          ) : null}
          <div className={formFooterActions}>
            <Button
              variant="secondary"
              size="sm"
              disabled={videoUploading}
              onClick={() => setLessonForm(null)}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={videoUploading || savingLesson || !lessonDraft.title.trim()}
              onClick={() => void saveLesson()}
            >
              {editing ? 'Salvar alterações' : 'Salvar aula'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  function renderLessonCard(module: MembersModule, lesson: MembersLesson, index: number) {
    const canDrag = module.lessons.length > 1
    return (
      <div
        key={lesson.id}
        className={lessonCard}
        data-testid={`members-lesson-${lesson.id}`}
        data-draggable={canDrag || undefined}
        data-dragging={draggingLessonId === lesson.id || undefined}
        data-drop={dropLessonId === lesson.id || undefined}
        draggable={canDrag}
        onDragStart={(event) => startLessonDrag(lesson.id, event)}
        onDragOver={(event) => overLesson(lesson.id, event)}
        onDrop={(event) => void dropLesson(module, lesson.id, event)}
        onDragEnd={() => {
          setDraggingLessonId(null)
          setDropLessonId(null)
        }}
      >
        {canDrag ? (
          <span className={dragHandle} aria-hidden>
            <GripVertical size={16} />
          </span>
        ) : null}
        <div className={lessonCardMain}>
          <span className={lessonCardTitle}>
            {index + 1}. {lesson.title}
          </span>
          <span className={lessonCardMeta}>
            {lessonKindLabel(lesson)} · {releaseLabel(lesson.releaseRule)}
          </span>
        </div>
        <div className={iconRow}>
          <button
            type="button"
            className={iconButton}
            aria-label={`Editar aula ${lesson.title}`}
            onClick={() => openEditLesson(module.id, lesson)}
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            className={iconButton}
            aria-label={`Excluir aula ${lesson.title}`}
            onClick={() => setPendingDelete({ type: 'lesson', moduleId: module.id, lesson })}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    )
  }

  const deleteDialog = pendingDelete ? deleteCopy(pendingDelete) : null

  return (
    <>
      <div className={editorStack}>
        {detail.modules.length === 0 && moduleDraft === null ? (
          <div className={emptyEditor}>
            <Layers size={20} aria-hidden />
            <strong>Nenhum módulo ainda</strong>
            <span>Comece criando o primeiro módulo. Depois, adicione as aulas dentro dele.</span>
          </div>
        ) : null}

        <div className={moduleList}>
          {detail.modules.map((module, index) => {
            const isRenaming = renaming?.moduleId === module.id
            const formOpenHere = lessonForm?.moduleId === module.id
            return (
              <section
                key={module.id}
                className={moduleBlock}
                data-testid={`members-module-${module.id}`}
                aria-label={`Módulo ${index + 1}: ${module.title}`}
              >
                <div className={moduleHeader}>
                  <div className={moduleHeading}>
                    <span className={moduleLabel}>Módulo {index + 1}</span>
                    {isRenaming ? (
                      <TextField
                        aria-label="Nome do módulo"
                        autoFocus
                        value={renaming.title}
                        onChange={(event) =>
                          setRenaming({ moduleId: module.id, title: event.target.value })
                        }
                        onBlur={() => void confirmRename()}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') void confirmRename()
                          if (event.key === 'Escape') setRenaming(null)
                        }}
                      />
                    ) : (
                      <h3 className={moduleTitle}>{module.title}</h3>
                    )}
                  </div>
                  <div className={iconRow}>
                    <button
                      type="button"
                      className={iconButton}
                      aria-label={`Subir módulo ${module.title}`}
                      disabled={index === 0}
                      onClick={() => void moveModule(module.id, -1)}
                    >
                      <ChevronUp size={15} />
                    </button>
                    <button
                      type="button"
                      className={iconButton}
                      aria-label={`Descer módulo ${module.title}`}
                      disabled={index === detail.modules.length - 1}
                      onClick={() => void moveModule(module.id, 1)}
                    >
                      <ChevronDown size={15} />
                    </button>
                    <button
                      type="button"
                      className={iconButton}
                      aria-label={`Renomear módulo ${module.title}`}
                      onClick={() => setRenaming({ moduleId: module.id, title: module.title })}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      className={iconButton}
                      aria-label={`Excluir módulo ${module.title}`}
                      onClick={() => setPendingDelete({ type: 'module', module })}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                <div className={lessonList}>
                  {module.lessons.length === 0 && !formOpenHere ? (
                    <p className={muted}>Este módulo ainda não tem aulas.</p>
                  ) : null}
                  {module.lessons.map((lesson, lessonIndex) =>
                    formOpenHere && lessonForm?.lessonId === lesson.id ? (
                      <div key={lesson.id}>{renderLessonForm(lesson)}</div>
                    ) : (
                      renderLessonCard(module, lesson, lessonIndex)
                    ),
                  )}
                  {formOpenHere && lessonForm?.lessonId === null ? (
                    renderLessonForm(null)
                  ) : (
                    <button
                      type="button"
                      className={addLessonButton}
                      onClick={() => openNewLesson(module.id)}
                    >
                      <Plus size={14} aria-hidden />
                      Adicionar aula
                    </button>
                  )}
                </div>
              </section>
            )
          })}
        </div>

        {moduleDraft !== null ? (
          <div className={formCard} data-testid="members-module-form">
            <div className={formHeader}>
              <span className={formTitle}>Novo módulo</span>
            </div>
            <TextField
              label="Nome do módulo"
              placeholder="Ex.: Fundamentos"
              autoFocus
              value={moduleDraft}
              onChange={(event) => setModuleDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void confirmModule()
                if (event.key === 'Escape') setModuleDraft(null)
              }}
            />
            <div className={formFooter}>
              <span className={muted}>Você adiciona as aulas logo depois.</span>
              <div className={formFooterActions}>
                <Button variant="secondary" size="sm" onClick={() => setModuleDraft(null)}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  disabled={createModule.isPending || !moduleDraft.trim()}
                  onClick={() => void confirmModule()}
                >
                  Criar módulo
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className={courseActions}>
            <Button
              variant="secondary"
              leftIcon={<Plus size={16} />}
              onClick={() => setModuleDraft('')}
            >
              Novo módulo
            </Button>
          </div>
        )}
      </div>
      <DeleteConfirmDialog
        open={deleteDialog !== null}
        title={deleteDialog?.title ?? ''}
        description={deleteDialog?.description ?? ''}
        confirmLabel={deleteDialog?.confirmLabel ?? 'Excluir'}
        pending={deleteModule.isPending || deleteLesson.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmPendingDelete()}
      />
    </>
  )
}
